/**
 * The 47 foods (research R§5 "Food values": table $A936, names from geon's editor) and the items a
 * killed monster leaves (tables $A790 / $A79A). An item's `item` field indexes FOODS for its picture
 * and name; its `value` is its own (a death item is worth more than the same food from a level slot).
 */

export interface Food {
  /** geon's editor name, as the renderer shows it. */
  name: string;
  /** Displayed points when it is a level's timed food item (02 §7.3). */
  points: number;
}

export const FOODS: readonly Food[] = [
  { name: 'eggplant', points: 10 },
  { name: 'egg', points: 40 },
  { name: 'turnip', points: 50 },
  { name: 'peach', points: 50 },
  { name: 'onion', points: 70 },
  { name: 'carrot', points: 70 },
  { name: 'boxnut', points: 200 },
  { name: 'purple gem', points: 350 },
  { name: 'banana', points: 500 },
  { name: 'fries', points: 500 },
  { name: 'strawberry', points: 500 },
  { name: 'beet', points: 700 },
  { name: 'cherries', points: 700 },
  { name: 'cake slice', points: 750 },
  { name: 'red gem', points: 850 },
  { name: 'donut', points: 1000 },
  { name: 'blue sherbet', points: 1000 },
  { name: 'orange', points: 1000 },
  { name: 'tuna sushi', points: 1000 },
  { name: 'ice cream', points: 1000 },
  { name: 'green locket', points: 1000 },
  { name: 'beer', points: 1000 },
  { name: 'burger', points: 2000 },
  { name: 'icepop', points: 2000 },
  { name: 'watermelon', points: 2000 },
  { name: 'white sherbet', points: 2000 },
  { name: 'red locket', points: 2000 },
  { name: 'cupcake', points: 3000 },
  { name: 'chicken', points: 3000 },
  { name: 'pineapple', points: 3000 },
  { name: 'yellow sherbet', points: 3000 },
  { name: 'ramen', points: 3000 },
  { name: 'cone', points: 4000 },
  { name: 'persimmon', points: 4000 },
  { name: 'grapes', points: 5000 },
  { name: 'martini', points: 5000 },
  { name: 'flan', points: 6000 },
  { name: 'shrimp sushi', points: 6000 },
  { name: 'yellow queen', points: 7000 },
  { name: 'yellow shard', points: 7000 },
  { name: 'red queen', points: 8000 },
  { name: 'yellow diamond', points: 8000 },
  { name: 'green queen', points: 9000 },
  { name: 'green diamond', points: 9000 },
  { name: 'purple diamond', points: 10000 },
  { name: "chack'n", points: 10000 },
  { name: 'corn', points: 12000 },
];

/** A food's name, for the renderer ('' outside the table). */
export const foodName = (index: number): string => FOODS[index]?.name ?? '';

export interface Prize {
  /** Index into FOODS: the picture. */
  food: number;
  /** Stored score units (displayed / 10). */
  units: number;
}

/**
 * What a killed monster leaves, by the C64's $8890 index (D_A790 item, D_A79A value; D_29C0 adds a
 * value of $50 to the low byte, others one byte up, x 100): 1..6 its place in the chain, 7 the water,
 * 8 fire, 9 anything else (lightning, bomb, lamp, cross, red-cross shot), 10 touched under the heart.
 */
export const DEATH_PRIZES: readonly Prize[] = [
  { food: 34, units: 0 },
  { food: 8, units: 50 },
  { food: 17, units: 100 },
  { food: 2, units: 200 },
  { food: 24, units: 300 },
  { food: 10, units: 400 },
  { food: 29, units: 500 },
  { food: 41, units: 800 },
  { food: 43, units: 900 },
  { food: 44, units: 1000 },
  { food: 34, units: 300 },
];

/** $8890 values for the deaths that are not chain positions (spawn_position_init, D_1A6F, $2800). */
export const DIED_IN_WATER = 7;
export const DIED_IN_FIRE = 8;
export const DIED_OTHERWISE = 9;
export const DIED_UNDER_HEART = 10;

/** The death item for an $8890 index, clamped into the table. */
export const deathPrize = (index: number): Prize =>
  DEATH_PRIZES[Math.max(1, Math.min(DEATH_PRIZES.length - 1, index))] ?? { food: 8, units: 50 };
